// src/components/calendar/edit/FreeDay.js
import { useContext } from "react";
import { enqueueSnackbar } from "notistack";
import axiosInstance from "../../../config/axiosInstance";
import { logApiError } from "../../../config/apiErrorHandling";
import { CalendarContext } from "../../context/CalendarContext";

const buildFreeDayReplaceScopes = ({ ci, day, periodStart }) => {
  const scopes = [
    { kind: "employee_day", ci: String(ci), day },
    { kind: "day", day },
    { kind: "employee_period", ci: String(ci), category: "rest_between_days" },
    { kind: "employee_period", ci: String(ci), category: "freeday_pattern" },
  ];
  if (periodStart && day === periodStart) {
    scopes.push({ kind: "employee_period", ci: String(ci), category: "rotation_opening" });
  }
  return scopes;
};

export const useHandleToggleFreeDay = () => {
  const {
    calendarData,
    setCalendarData,
    mergeCalendarValidationPatch,
  } = useContext(CalendarContext);

  const handleToggleFreeDay = async (
    ci,
    day,
    free_day,
    station_id,
    position_id,
    setLoadingFreeDay = () => {},
    onClose = () => {},
    confirm = false,
    carryForward = true
  ) => {
    setLoadingFreeDay(true);

    if (!ci || !day || !station_id || !position_id) {
      enqueueSnackbar("Error: Datos insuficientes para modificar el día libre", { variant: "error" });
      setLoadingFreeDay(false);
      return { ok: false };
    }

    const newFreeDayState = !free_day;

    try {
      const { data } = await axiosInstance.post(
        "/calendar/update_free_day",
        {},
        {
          params: {
            ci: String(ci),
            day: String(day),
            add_free_day: newFreeDayState,
            station_id: String(station_id),
            position_id: String(position_id),
            calendar_id: calendarData?._id ?? "",
            carry_forward: carryForward,
            ...(confirm ? { confirm: true } : {}),
          },
        }
      );

      if (data?.updated_calendar) {
        const scopeNote = carryForward
          ? " y se recalculó la secuencia de libres desde este día"
          : " como cambio puntual (no afecta las próximas quincenas)";
        enqueueSnackbar(
          `Día libre ${newFreeDayState ? "agregado" : "quitado"} correctamente${scopeNote}`,
          { variant: "success" }
        );

        // Actualización inmediata
        setCalendarData(data.updated_calendar);
        mergeCalendarValidationPatch({
          errors: data.errors,
          warnings: data.warnings,
          replaceScopes: buildFreeDayReplaceScopes({
            ci,
            day,
            periodStart: calendarData?.start_date,
          }),
        });
        return { ok: true, data };
      } else {
        enqueueSnackbar(data?.message || "Error al modificar el día libre", { variant: "error" });
        return { ok: false, data };
      }
    } catch (error) {
      const info = logApiError(
        "calendar.free_day.toggle",
        error,
        "No se pudo modificar el día libre. Intenta nuevamente."
      );
      if (!(info.status === 409 && info.needsConfirmation)) {
        enqueueSnackbar(info.message, { variant: info.status === 409 ? "warning" : "error" });
      }
      return { ok: false, error, info };
    } finally {
      setLoadingFreeDay(false);
      onClose();
    }
  };

  return handleToggleFreeDay;
};
